<template>
  <div class="app-main-content web-order-list">
    <!-- 列表视图 -->
    <div v-show="view === 'list'" class="grid-view">
      <div class="main-content">
        <!-- 搜索栏（resetKey 用于重置时重建非受控的 advance-select） -->
        <div :key="filterResetKey" class="section filter-bar">
          <!-- 操作按钮：查询/重置/接单/拒绝，置顶一排 -->
          <div class="wo-filter-actions">
            <el-button type="primary" @click="onSearch">查询</el-button>
            <el-button @click="onReset">重置</el-button>
            <el-button type="primary" icon="el-icon-check" :disabled="!canAccept" @click="onAcceptSelected">接单</el-button>
            <el-button type="danger" icon="el-icon-close" :disabled="!canReject" @click="openRejectBatch">拒绝</el-button>
            <span v-if="selectedRows.length" class="wo-toolbar-tip">已选 {{ selectedRows.length }} 单</span>
          </div>
          <div class="wo-filter-fields">
            <el-input v-model="filter.orderNo" placeholder="订单编号" clearable class="wo-filter-item" @keyup.enter.native="onSearch" />
            <el-input v-model="filter.businessNo" placeholder="客户业务编号" clearable class="wo-filter-item" @keyup.enter.native="onSearch" />
            <!-- 委托单位：后端按 customerId(cmpIdx) 过滤，故用委托单位下拉而非名称文本 -->
            <cargo-advance-select
              :select-type="selectTypeNames.basic_data_Company"
              filterable
              placeholder="委托单位"
              class="wo-filter-item"
              @parentCallBack="p => { filter.customerId = (p && p.value) || null }"
              @clear="() => { filter.customerId = null }"
            />
            <cargo-advance-select
              :select-type="selectTypeNames.basic_data_Seaport"
              filterable
              placeholder="起运港 POL"
              :custom-label="(item) => `[${item.code}]${item.englishName}`"
              :filter-tag-names="['code','englishName']"
              :show-custom-label-in-option="true"
              class="wo-filter-item"
              @parentCallBack="p => { filter.pol = (p && p.value) || '' }"
              @clear="() => { filter.pol = '' }"
            />
            <cargo-advance-select
              :select-type="selectTypeNames.basic_data_Seaport"
              filterable
              placeholder="目的港 POD"
              :custom-label="(item) => `[${item.code}]${item.englishName}`"
              :filter-tag-names="['code','englishName']"
              :show-custom-label-in-option="true"
              class="wo-filter-item"
              @parentCallBack="p => { filter.pod = (p && p.value) || '' }"
              @clear="() => { filter.pod = '' }"
            />
            <el-select v-model="filter.orderStatus" placeholder="订单状态" clearable class="wo-filter-item">
              <el-option :value="0" label="待接单" />
              <el-option :value="1" label="已接单" />
              <el-option :value="2" label="已拒绝" />
            </el-select>
            <el-select v-model="filter.bookingAuditStatus" placeholder="订舱预审状态" clearable class="wo-filter-item">
              <el-option :value="0" label="待审核" />
              <el-option :value="1" label="已通过" />
              <el-option :value="2" label="已拒绝" />
            </el-select>
          </div>
        </div>
        <!-- 列表 -->
        <div class="section grid-wrapper">
          <cargo-ag-grid
            :grid-params.sync="gridParams"
            :grid-options="gridOptions"
            :column-defs="columnDefs"
            :row-data="rowData"
            @gridReady="onReady"
          />
        </div>
        <!-- 分页 -->
        <div class="pagination-wrapper">
          <cargo-pagination
            v-show="filter.total > 0"
            :total="filter.total"
            :page.sync="filter.pageNum"
            :limit.sync="filter.pageSize"
            :page-sizes="[10,20,30,50,100]"
            @pagination="toQuery"
          />
        </div>
      </div>
    </div>

    <!-- 批量拒绝 -->
    <el-dialog :visible.sync="rejectDialog.visible" title="批量拒绝订单" width="460px">
      <div v-if="rejectDialog.rows && rejectDialog.rows.length" style="margin-bottom:10px;color:#606266;">
        即将拒绝 <b>{{ rejectDialog.rows.length }}</b> 单：<br>
        <span v-for="r in rejectDialog.rows" :key="r.uuid" style="margin-right:8px;">{{ r.orderNo }}</span>
      </div>
      <el-input v-model="rejectDialog.reason" type="textarea" :rows="5" placeholder="请输入拒绝原因（对所有选中订单生效）" />
      <span slot="footer">
        <el-button @click="rejectDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="confirmReject">确定</el-button>
      </span>
    </el-dialog>

    <!-- 详情视图（新页面，非弹窗，只读，参考 booking 详情排版） -->
    <div v-show="view === 'detail'" class="web-order-detail">
      <!-- 顶部操作：返回列表 / 接单 / 拒绝（订单编号在下方基本信息里展示，此处不再重复） -->
      <div class="section wo-detail-toolbar">
        <el-button icon="el-icon-back" @click="backToList">返回列表</el-button>
        <el-button type="primary" icon="el-icon-check" :disabled="!canAcceptDetail" @click="onAcceptDetail">接单</el-button>
        <el-button type="danger" icon="el-icon-close" :disabled="!canRejectDetail" @click="openRejectDetail">拒绝</el-button>
      </div>

      <div v-loading="detailLoading" class="section wo-detail-body">
        <!-- 基本信息 -->
        <div class="wo-section-title">基本信息</div>
        <el-form :inline="true" label-position="right" label-width="96px" class="wo-detail-form">
          <el-form-item label="订单编号"><el-input :value="detailForm.orderNo" disabled style="width:200px" /></el-form-item>
          <el-form-item label="订单状态"><el-input :value="orderStatusName(detailForm.orderStatus)" disabled style="width:200px" /></el-form-item>
          <el-form-item label="客户名称"><el-input :value="detailForm.customerName" disabled style="width:200px" /></el-form-item>
          <el-form-item label="业务编号"><el-input :value="detailForm.businessNo" disabled style="width:200px" /></el-form-item>
          <el-form-item label="贸易条款"><el-input :value="detailForm.tradeTerms" disabled style="width:200px" /></el-form-item>
          <el-form-item label="订舱预审"><el-input :value="auditStatusName(detailForm.bookingAuditStatus)" disabled style="width:200px" /></el-form-item>
          <el-form-item label="关联工作单"><el-input :value="detailForm.jobNo" disabled style="width:200px" /></el-form-item>
          <el-form-item label="创建时间"><el-input :value="fmtDate(detailForm.dateCreated)" disabled style="width:200px" /></el-form-item>
        </el-form>

        <!-- 运输信息 -->
        <div class="wo-section-title">运输信息</div>
        <el-form :inline="true" label-position="right" label-width="96px" class="wo-detail-form">
          <el-form-item label="船名"><el-input :value="detailForm.vesselName" disabled style="width:200px" /></el-form-item>
          <el-form-item label="航次"><el-input :value="detailForm.voyage" disabled style="width:200px" /></el-form-item>
          <el-form-item label="船公司"><el-input :value="detailForm.carrier" disabled style="width:200px" /></el-form-item>
          <el-form-item label="起运港"><el-input :value="portName(detailForm.pol)" disabled style="width:200px" /></el-form-item>
          <el-form-item label="目的港"><el-input :value="portName(detailForm.pod)" disabled style="width:200px" /></el-form-item>
          <el-form-item label="ETD"><el-input :value="fmtDate(detailForm.etd)" disabled style="width:200px" /></el-form-item>
          <el-form-item label="货好时间"><el-input :value="fmtDate(detailForm.cargoReadyDate)" disabled style="width:200px" /></el-form-item>
        </el-form>

        <!-- 货物信息 -->
        <div class="wo-section-title">货物信息</div>
        <el-form :inline="true" label-position="right" label-width="96px" class="wo-detail-form">
          <el-form-item label="总件数(PCS)"><el-input :value="detailForm.totalPackages" disabled style="width:200px" /></el-form-item>
          <el-form-item label="总毛重(KGS)"><el-input :value="detailForm.totalGrossWeight" disabled style="width:200px" /></el-form-item>
          <el-form-item label="总体积(CBM)"><el-input :value="detailForm.totalVolume" disabled style="width:200px" /></el-form-item>
          <template v-if="hasCifFields">
            <el-form-item label="HS编码"><el-input :value="detailForm.hsCode" disabled style="width:200px" /></el-form-item>
            <el-form-item label="中文品名"><el-input :value="detailForm.cnGoodsName" disabled style="width:200px" /></el-form-item>
            <el-form-item label="英文品名"><el-input :value="detailForm.enGoodsName" disabled style="width:200px" /></el-form-item>
            <el-form-item label="货值"><el-input :value="detailForm.cargoValue" disabled style="width:200px" /></el-form-item>
            <el-form-item label="联系人"><el-input :value="detailForm.contactPerson" disabled style="width:200px" /></el-form-item>
            <el-form-item label="邮编"><el-input :value="detailForm.deliveryZipCode" disabled style="width:200px" /></el-form-item>
            <el-form-item label="城市代码"><el-input :value="detailForm.deliveryCityCode" disabled style="width:200px" /></el-form-item>
            <el-form-item label="派送地址"><el-input :value="detailForm.deliveryAddress" disabled style="width:520px" /></el-form-item>
          </template>
        </el-form>

        <!-- 箱型箱量 -->
        <div class="wo-section-title">箱型箱量</div>
        <el-table :data="detailForm.containers" border size="small" class="wo-containers-table">
          <el-table-column type="index" label="#" width="50" />
          <el-table-column prop="containerType" label="箱型" />
          <el-table-column prop="quantity" label="箱量" width="120" />
        </el-table>

        <!-- 收发通 -->
        <template v-if="parties.length">
          <div class="wo-section-title">收发通</div>
          <el-row :gutter="12">
            <el-col v-for="p in parties" :key="p.title" :span="8">
              <div class="party-card">
                <div class="party-title">{{ p.title }}<span v-if="p.code" class="party-code"> [{{ p.code }}]</span></div>
                <div v-for="line in p.lines" :key="line.label" class="party-line">
                  <span class="party-label">{{ line.label }}：</span>{{ line.value || '—' }}
                </div>
              </div>
            </el-col>
          </el-row>
        </template>

        <!-- 拒绝原因 -->
        <template v-if="detailForm.rejectReason">
          <div class="wo-section-title">拒绝原因</div>
          <el-input :value="detailForm.rejectReason" type="textarea" :rows="2" disabled />
        </template>

        <!-- 备注 -->
        <template v-if="detailForm.remark">
          <div class="wo-section-title">备注</div>
          <el-input :value="detailForm.remark" type="textarea" :rows="2" disabled />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { getLoading } from '@/utils/utilBaseElement'
import cargoAdvanceSelect from '@/components/Cargo/Common/cargoAdvanceSelect'
import { pageWebOrder, getWebOrder, acceptWebOrder, rejectWebOrder } from '@/api/webOrder/webOrder'
import ColumnUtils from '@/utils/ColumnUtils'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'

// 订单状态：0待接单 1已接单 2已拒绝（撤回3 已被后端 pageForManage 过滤）
const ORDER_STATUS = { 0: '待接单', 1: '已接单', 2: '已拒绝', 3: '撤回' }
const AUDIT_STATUS = { 0: '待审核', 1: '已通过', 2: '已拒绝' }
const PARTY_LINES = [
  { key: 'Line1', label: '企业名称' },
  { key: 'Line2', label: '地址' },
  { key: 'Line3', label: '联系方式' },
  { key: 'Line4', label: '城市' },
  { key: 'Line5', label: '省份' },
  { key: 'Line6', label: '国家' },
  { key: 'Line7', label: '邮编' }
]

export default {
  name: 'WebOrder',
  components: { cargoAdvanceSelect },
  data() {
    return {
      selectTypeNames: TYPE_NAMES,
      gridParams: {},
      gridApi: null,
      filter: {
        total: 0, pageNum: 1, pageSize: 20,
        orderNo: '', businessNo: '', customerId: null, pol: '', pod: '',
        orderStatus: null, bookingAuditStatus: null
      },
      filterResetKey: 0,
      rowData: [],
      loading: null,
      view: 'list', // 'list' | 'detail'
      selectedRows: [],
      rejectDialog: { visible: false, reason: '', rows: [] },
      detailLoading: false,
      detailForm: {},
      gridOptions: {
        rowSelection: 'multiple',
        suppressRowClickSelection: true, // 仅复选框选中，避免与双击查看详情冲突
        suppressCellSelection: true,
        context: { componentParent: this },
        defaultColDef: { resizable: true },
        onRowDoubleClicked: (event) => { if (event && event.data) this.openDetail(event.data) },
        onSelectionChanged: () => { this.selectedRows = this.gridApi ? this.gridApi.getSelectedRows() : [] }
      },
      columnDefs: [
        { headerCheckboxSelection: true, checkboxSelection: true, width: 42, sortable: false, suppressMenu: true },
        { headerName: '订单编号', field: 'orderNo', width: 160 },
        { headerName: '客户名称', field: 'customerName', width: 150 },
        { headerName: '客户业务编号', field: 'businessNo', width: 140 },
        { headerName: '贸易条款', field: 'tradeTerms', width: 90 },
        { headerName: '船名', field: 'vesselName', width: 120 },
        { headerName: '航次', field: 'voyage', width: 90 },
        { headerName: 'POL', field: 'pol', width: 110 },
        { headerName: 'POD', field: 'pod', width: 110 },
        { headerName: '箱型箱量', width: 170,
          cellRenderer: p => this.containersText(p.data && p.data.containers) },
        { headerName: '件数', field: 'totalPackages', width: 100,
          valueFormatter: p => this.unitVal(p.data.totalPackages, 'PCS') },
        { headerName: '毛重', field: 'totalGrossWeight', width: 110,
          valueFormatter: p => this.unitVal(p.data.totalGrossWeight, 'KGS') },
        { headerName: '体积', field: 'totalVolume', width: 110,
          valueFormatter: p => this.unitVal(p.data.totalVolume, 'CBM') },
        { headerName: '订单状态', field: 'orderStatus', width: 100, cellRenderer: p => ORDER_STATUS[p.value] || '' },
        { headerName: '订舱预审', field: 'bookingAuditStatus', width: 100, cellRenderer: p => AUDIT_STATUS[p.value] || '' },
        { headerName: '关联工作单', field: 'jobNo', width: 130 }
      ]
    }
  },
  computed: {
    // 接单：仅当选中 1 单且为待接单(0)时可用
    canAccept() {
      return this.selectedRows.length === 1 && this.selectedRows[0].orderStatus === 0
    },
    // 拒绝：批量，选中≥1 且每行均可拒绝（待接单(0)，或 已接单(1)+订舱预审已拒绝(2)）
    canReject() {
      return this.selectedRows.length > 0 && this.selectedRows.every(r => this.isRejectable(r))
    },
    // 详情页：仅待接单(0)可接单
    canAcceptDetail() { return this.detailForm && this.detailForm.orderStatus === 0 },
    // 详情页拒绝：待接单(0)；或 已接单(1) 且 订舱预审已拒绝(2)
    canRejectDetail() { return this.detailForm && this.isRejectable(this.detailForm) },
    hasCifFields() {
      const f = this.detailForm || {}
      return !!(f.hsCode || f.cnGoodsName || f.enGoodsName || f.deliveryAddress)
    },
    parties() {
      const f = this.detailForm || {}
      const build = (title, prefix) => ({
        title,
        code: f[prefix + 'Code'],
        lines: PARTY_LINES.map(l => ({ label: l.label, value: f[prefix + l.key] }))
      })
      return [
        build('发货人', 'shipper'),
        build('收货人', 'consignee'),
        build('通知人', 'notify')
      ].filter(p => p.code || p.lines.some(l => l.value))
    }
  },
  created() { this.toQuery() },
  methods: {
    onReady(params) { this.gridApi = params.api },
    onSearch() { this.filter.pageNum = 1; this.toQuery() },
    onReset() {
      this.filter.orderNo = ''
      this.filter.businessNo = ''
      this.filter.customerId = null
      this.filter.pol = ''
      this.filter.pod = ''
      this.filter.orderStatus = null
      this.filter.bookingAuditStatus = null
      this.filterResetKey++ // 重建非受控的 advance-select，清空已选展示
      this.onSearch()
    },
    toQuery() {
      this.loading = getLoading()
      pageWebOrder({
        orderNo: this.filter.orderNo,
        businessNo: this.filter.businessNo,
        customerId: this.filter.customerId,
        pol: this.filter.pol,
        pod: this.filter.pod,
        orderStatus: this.filter.orderStatus,
        bookingAuditStatus: this.filter.bookingAuditStatus,
        pageNum: this.filter.pageNum,
        pageSize: this.filter.pageSize
      }).then(res => this.handleQueryData(res))
        .catch(() => { this.loading && this.loading.close() })
    },
    handleQueryData(res) {
      this.rowData = (res.data && res.data.list) || []
      this.filter.pageNum = res.data.pageNum
      this.filter.pageSize = res.data.pageSize
      this.filter.total = Number(res.data.total)
      this.selectedRows = [] // 刷新后清空选中
      this.loading && this.loading.close()
    },
    containersText(cs) {
      if (!cs || !cs.length) return ''
      return cs.map(c => `${c.quantity}*${c.containerType}`).join(';')
    },
    // 值 + 单位拼接（空值返回空串）
    unitVal(v, unit) {
      return (v !== null && v !== undefined && v !== '') ? `${v} ${unit}` : ''
    },
    orderStatusName(code) { return ORDER_STATUS[code] || '' },
    auditStatusName(code) { return AUDIT_STATUS[code] || '' },
    // 单行是否可拒绝：待接单(0)；或 已接单(1) 且 订舱预审已拒绝(2)
    isRejectable(row) {
      return row && (row.orderStatus === 0 || (row.orderStatus === 1 && row.bookingAuditStatus === 2))
    },
    portName(code) { return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Seaport, 'code', code, 'englishName') },
    fmtDate(v) { return ColumnUtils.dateLabelFunc(v) },
    onAcceptSelected() {
      if (!this.canAccept) return
      this.doAccept(this.selectedRows[0].uuid)
    },
    // 接单：accept 拿映射的 SeaExportJobDetailVO → 挂 sourceOrderId → 跳转 RemoteBooking（webAccept 新增页）。
    // 默认值（lob/containerLoadType/freightTerm/...）由 remoteBooking 的 webAccept 分支补全（与 SE/AE 分支一致）。
    doAccept(uuid) {
      const loading = getLoading()
      acceptWebOrder(uuid).then(res => {
        const job = res.data || {}
        job.webAcceptSourceOrderId = uuid
        this.$router.push({ name: 'RemoteBooking', params: { view: 'detail', source: 'webAccept', action: 'add', job }})
      }).finally(() => loading.close())
    },
    openRejectBatch() {
      if (!this.canReject) return
      this.rejectDialog.rows = this.selectedRows.slice()
      this.rejectDialog.reason = ''
      this.rejectDialog.visible = true
    },
    confirmReject() {
      const reason = (this.rejectDialog.reason || '').trim()
      if (!reason) { this.$message.warning('请输入拒绝原因'); return }
      const rows = this.rejectDialog.rows || []
      Promise.all(rows.map(r => rejectWebOrder(r.uuid, reason))).then(() => {
        this.$message.success(`已拒绝 ${rows.length} 单`)
        this.rejectDialog.visible = false
        this.view = 'list'
        this.toQuery()
      })
    },
    openDetail(row) {
      this.view = 'detail'
      this.detailForm = {}
      this.detailLoading = true
      getWebOrder(row.uuid).then(res => {
        this.detailForm = Object.assign({ containers: [] }, res.data)
        this.detailLoading = false
      }).catch(() => { this.detailLoading = false })
    },
    backToList() { this.view = 'list' },
    onAcceptDetail() {
      if (!this.canAcceptDetail) return
      this.doAccept(this.detailForm.uuid)
    },
    openRejectDetail() {
      if (!this.canRejectDetail) return
      this.rejectDialog.rows = [this.detailForm]
      this.rejectDialog.reason = ''
      this.rejectDialog.visible = true
    }
  }
}
</script>

<style scoped>
.web-order-list .filter-bar { display:flex; flex-direction:column; gap:8px; }
.web-order-list .filter-bar .wo-filter-actions { display:flex; justify-content:flex-start; }
.web-order-list .filter-bar .wo-filter-fields { display:flex; flex-wrap:wrap; gap:8px 10px; align-items:center; }
.web-order-list .filter-bar .wo-filter-item { width: 200px; }
.web-order-list .wo-toolbar-tip { color:#909399; font-size:12px; }
.web-order-list .wo-detail-toolbar { display:flex; flex-direction:row; align-items:center; gap:10px; }
.web-order-list .wo-detail-body { padding:12px 16px; }
.web-order-list .wo-section-title { position:relative; padding-left:10px; margin:16px 0 10px; font-size:13px; font-weight:600; color:#303133; line-height:18px; }
.web-order-list .wo-section-title::before { content:''; position:absolute; left:0; top:1px; bottom:1px; width:4px; background:#1F75D5; border-radius:2px; }
.web-order-list .wo-detail-body .wo-section-title:first-child { margin-top:0; }
.web-order-list .wo-detail-form .el-form-item { margin-right:8px; margin-bottom:10px; }
.web-order-list .wo-containers-table { max-width:480px; }
.web-order-list .party-card { border:1px solid #ebeef5; border-radius:4px; padding:8px 10px; }
.web-order-list .party-title { font-weight:600; margin-bottom:6px; }
.web-order-list .party-code { color:#909399; font-weight:normal; }
.web-order-list .party-line { line-height:20px; font-size:12px; }
.web-order-list .party-label { color:#909399; display:inline-block; width:70px; }
</style>
<style>
.web-order-list .wo-act { color:#0A0AFF; text-decoration:underline; cursor:pointer; margin-right:4px; }
</style>
